import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, User, Users, CheckCircle, Clock, 
  ArrowRight, Wallet, TrendingUp, AlertCircle, Send, Check, CreditCard, ExternalLink,
  ChevronDown, ChevronUp, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import usePermissions from '@/hooks/usePermissions';
import ManagerCashPanel from '@/components/ManagerCashPanel';
import AdminCashPanel from '@/components/AdminCashPanel';
import ShopifyPaidSyncLog from '@/components/ShopifyPaidSyncLog';
import CourierCodPanel from '@/components/CourierCodPanel';
import PaymentVerificationPanel from '@/components/PaymentVerificationPanel';
import PosShiftsPanel from '@/components/PosShiftsPanel';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CashManagementPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const { hasPermission, isAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  
  // Driver state
  const [driverPending, setDriverPending] = useState({ 
    pending_cash: 0, 
    pending_approval: 0,
    orders: [], 
    orders_pending_approval: [] 
  });
  
  // Manager state
  const [managerPending, setManagerPending] = useState({ drivers: [] });
  const [managerToAdmin, setManagerToAdmin] = useState({
    total_to_handover: 0,
    total_pending_approval: 0,
    orders_to_handover: [],
    orders_pending_approval: []
  });
  
  // Admin state
  const [adminPending, setAdminPending] = useState({ total_pending: 0, orders: [] });
  const [allDriversCash, setAllDriversCash] = useState({ total_cash_with_drivers: 0, drivers: [] });
  
  // MamoPay payments state
  const [mamoPayments, setMamoPayments] = useState([]);
  const [mamoLoading, setMamoLoading] = useState(false);
  
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedManagerOrders, setSelectedManagerOrders] = useState([]);
  const [expandedDrivers, setExpandedDrivers] = useState({}); // Track expanded driver cards
  const adminCashRef = useRef(null);

  // Permission-based access
  const canViewCash = hasPermission('cash.view');
  const canCollectFromDriver = hasPermission('cash.collect_from_driver');
  const canCollectFromManager = hasPermission('cash.collect_from_manager');
  const canCollectCash = hasPermission('deliveries.collect_cash');

  useEffect(() => {
    loadCashData();
  }, [canViewCash, canCollectFromDriver, canCollectFromManager, canCollectCash]);

  const loadCashData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Driver view - show pending cash for current user
      if (canViewCash || canCollectCash) {
        try {
          const res = await axios.get(`${API_BASE_URL}/cash/driver-pending`, { headers });
          setDriverPending(res.data);
        } catch (err) {
          console.log('Driver pending not available');
        }
      }

      // Manager view - pending approvals from drivers
      if (canCollectFromDriver || isAdmin) {
        try {
          const res = await axios.get(`${API_BASE_URL}/cash/manager-pending`, { headers });
          setManagerPending(res.data);
        } catch (err) {
          console.log('Manager pending not available');
        }
        
        // Manager's own cash to hand to admin
        try {
          const res = await axios.get(`${API_BASE_URL}/cash/manager-to-admin`, { headers });
          setManagerToAdmin(res.data);
        } catch (err) {
          console.log('Manager to admin not available');
        }
      }

      // Admin view - pending approvals from managers
      if (canCollectFromManager || isAdmin) {
        try {
          const res = await axios.get(`${API_BASE_URL}/cash/admin-pending`, { headers });
          setAdminPending(res.data);
        } catch (err) {
          console.log('Admin pending not available');
        }
      }
      
      // Admin/Manager view - total cash with all drivers
      if (canCollectFromDriver || canCollectFromManager || isAdmin) {
        try {
          const res = await axios.get(`${API_BASE_URL}/cash/all-drivers-summary`, { headers });
          setAllDriversCash(res.data);
        } catch (err) {
          console.log('All drivers cash not available');
        }
      }
      
      // Load Link Payments (Mamo + Stripe) — single unified endpoint.
      if (isAdmin || canCollectFromManager || canCollectFromDriver) {
        try {
          setMamoLoading(true);
          const res = await axios.get(`${API_BASE_URL}/payments/link-payments?limit=200`, { headers });
          setMamoPayments(res.data.payments || res.data.orders || []);
        } catch (err) {
          console.log('Link payments not available');
        } finally {
          setMamoLoading(false);
        }
      }
    } catch (error) {
      toast.error('Failed to load cash data');
    } finally {
      setLoading(false);
    }
  };

  // Driver: Request handover to manager
  const handleRequestHandover = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders to hand over');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/cash/request-handover`, selectedOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Handover request sent to manager');
      setSelectedOrders([]);
      loadCashData();
    } catch (error) {
      toast.error('Failed to request handover');
    }
  };

  // Manager: Approve driver's handover
  const handleApproveDriverHandover = async (orderIds) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/cash/approve-driver-handover`, orderIds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cash handover approved');
      loadCashData();
    } catch (error) {
      toast.error('Failed to approve handover');
    }
  };

  // Manager: Request handover to admin
  const handleRequestAdminHandover = async () => {
    if (selectedManagerOrders.length === 0) {
      toast.error('Please select orders to hand over');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/cash/request-admin-handover`, selectedManagerOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Handover request sent to admin');
      setSelectedManagerOrders([]);
      loadCashData();
    } catch (error) {
      toast.error('Failed to request handover');
    }
  };

  // Admin: Approve manager's handover
  const handleApproveManagerHandover = async (orderIds) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/cash/approve-manager-handover`, orderIds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cash handover approved');
      loadCashData();
    } catch (error) {
      toast.error('Failed to approve handover');
    }
  };

  const toggleOrderSelection = (orderId, list = 'driver') => {
    if (list === 'driver') {
      setSelectedOrders(prev => 
        prev.includes(orderId) 
          ? prev.filter(id => id !== orderId)
          : [...prev, orderId]
      );
    } else {
      setSelectedManagerOrders(prev => 
        prev.includes(orderId) 
          ? prev.filter(id => id !== orderId)
          : [...prev, orderId]
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Cash Management</h1>
        <Button onClick={loadCashData} variant="outline" className="border-wt-border text-wt-text-primary">
          Refresh
        </Button>
      </div>

      {/* ============ ADMIN/MANAGER VIEW - ALL DRIVERS CASH ============ */}
      {(isAdmin || canCollectFromDriver || canCollectFromManager) && allDriversCash.total_cash_with_drivers > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-orange-500/10">
              <Users className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Total Cash With Drivers</h2>
              <p className="text-wt-text-muted text-sm">{allDriversCash.total_drivers || 0} driver(s) holding cash</p>
            </div>
          </div>

          <div className="text-4xl font-bold text-orange-400 mb-6">
            {formatCurrency(allDriversCash.total_cash_with_drivers || 0)}
          </div>

          {allDriversCash.drivers?.length > 0 && (
            <div className="space-y-3">
              {allDriversCash.drivers.map(driver => (
                <div key={driver.driver_id} className="bg-wt-subtle rounded-lg overflow-hidden">
                  {/* Driver Header - Clickable */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-wt-bg/50 transition-colors"
                    onClick={() => setExpandedDrivers(prev => ({
                      ...prev,
                      [driver.driver_id]: !prev[driver.driver_id]
                    }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-wt-text-muted" />
                        <span className="text-wt-text-primary font-medium">{driver.driver_name}</span>
                        {expandedDrivers[driver.driver_id] ? (
                          <ChevronUp className="h-4 w-4 text-wt-text-muted" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-wt-text-muted" />
                        )}
                      </div>
                      <span className="text-orange-400 font-semibold">{formatCurrency(driver.total_cash)}</span>
                    </div>
                    <div className="flex gap-4 text-xs text-wt-text-muted">
                      <span>{driver.order_count} orders</span>
                      {driver.pending_handover > 0 && (
                        <span className="text-yellow-400">Pending: {formatCurrency(driver.pending_handover)}</span>
                      )}
                      {driver.awaiting_approval > 0 && (
                        <span className="text-blue-400">Awaiting: {formatCurrency(driver.awaiting_approval)}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Order List */}
                  <AnimatePresence>
                    {expandedDrivers[driver.driver_id] && driver.orders?.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-wt-border"
                      >
                        <div className="p-3 space-y-2 bg-wt-bg/30">
                          <div className="text-xs text-wt-text-muted font-medium px-2 mb-2">
                            Order Details
                          </div>
                          {driver.orders.map(order => (
                            <div 
                              key={order.order_id} 
                              className="flex items-center justify-between p-2 bg-wt-paper rounded-lg border border-wt-border"
                            >
                              <div className="flex items-center gap-3">
                                <Package className="h-4 w-4 text-wt-text-muted" />
                                <div>
                                  <span className="text-wt-text-primary font-medium">{order.order_number}</span>
                                  {order.customer_name && (
                                    <p className="text-xs text-wt-text-muted">{order.customer_name}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-orange-400 font-medium">{formatCurrency(order.cash_amount)}</span>
                                {order.handover_requested ? (
                                  <Badge className="ml-2 bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                    Requested
                                  </Badge>
                                ) : (
                                  <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                                    Pending
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ============ DRIVER VIEW ============ */}
      {(canViewCash || canCollectCash) && !isAdmin && (
        <>
          {/* Pending Cash to Request Handover */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-wt-gold/10">
                <Wallet className="h-6 w-6 text-wt-gold" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-wt-text-primary">Cash Ready to Hand Over</h2>
                <p className="text-wt-text-muted text-sm">Request handover to manager for approval</p>
              </div>
            </div>

            <div className="text-4xl font-bold text-wt-gold mb-6">
              {formatCurrency(driverPending.pending_cash || 0)}
            </div>

            {driverPending.orders?.length > 0 ? (
              <div className="space-y-3">
                {driverPending.orders.map(order => (
                  <div key={order.id} className="flex items-center gap-3 p-4 bg-wt-subtle rounded-lg">
                    <Checkbox
                      checked={selectedOrders.includes(order.id)}
                      onCheckedChange={() => toggleOrderSelection(order.id, 'driver')}
                      className="border-wt-gold data-[state=checked]:bg-wt-gold"
                    />
                    <div className="flex-1">
                      <p className="text-wt-text-primary font-medium">{order.order_number}</p>
                      <p className="text-sm text-wt-text-muted">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-wt-gold font-semibold">{formatCurrency(order.cash_amount)}</p>
                      <p className="text-xs text-wt-text-muted">
                        {order.collected_at && new Date(order.collected_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}

                <Button
                  onClick={handleRequestHandover}
                  disabled={selectedOrders.length === 0}
                  className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black mt-4"
                  data-testid="request-handover-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Request Handover ({selectedOrders.length}) to Manager
                </Button>
              </div>
            ) : (
              <p className="text-wt-text-muted text-center py-4">No cash pending handover</p>
            )}
          </motion.div>

          {/* Pending Approval from Manager */}
          {driverPending.orders_pending_approval?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-yellow-500/30 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg bg-yellow-500/10">
                  <Clock className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-wt-text-primary">Pending Manager Approval</h2>
                  <p className="text-wt-text-muted text-sm">Waiting for manager to confirm receipt</p>
                </div>
                <Badge className="ml-auto bg-yellow-500/20 text-yellow-400">
                  {formatCurrency(driverPending.pending_approval || 0)}
                </Badge>
              </div>

              <div className="space-y-3">
                {driverPending.orders_pending_approval.map(order => (
                  <div key={order.id} className="flex items-center gap-3 p-4 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                    <Clock className="h-5 w-5 text-yellow-500" />
                    <div className="flex-1">
                      <p className="text-wt-text-primary font-medium">{order.order_number}</p>
                      <p className="text-sm text-wt-text-muted">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400 font-semibold">{formatCurrency(order.cash_amount)}</p>
                      <p className="text-xs text-wt-text-muted">
                        Requested: {order.requested_at && new Date(order.requested_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* ============ MANAGER VIEW ============ */}
      {(canCollectFromDriver || isAdmin) && (
        <>
          {/* Approve Driver Handovers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-green-500/10">
                <User className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-wt-text-primary">Driver Handover Requests</h2>
                <p className="text-wt-text-muted text-sm">Approve cash received from drivers</p>
              </div>
            </div>

            {managerPending.drivers?.length > 0 ? (
              <div className="space-y-4">
                {managerPending.drivers.map(driver => (
                  <div key={driver.driver_id} className="border border-wt-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-wt-text-muted" />
                        <span className="text-wt-text-primary font-medium">{driver.driver_name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">{formatCurrency(driver.total_pending)}</p>
                        <p className="text-xs text-wt-text-muted">{driver.orders.length} orders</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {driver.orders.map(order => (
                        <div key={order.id} className="flex justify-between text-sm p-2 bg-wt-subtle rounded">
                          <span className="text-wt-text-muted">{order.order_number}</span>
                          <span className="text-wt-text-primary">{formatCurrency(order.cash_amount)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      onClick={() => handleApproveDriverHandover(driver.orders.map(o => o.id))}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-driver-${driver.driver_id}`}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Approve & Receive {formatCurrency(driver.total_pending)}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-wt-text-muted text-center py-4">No pending handover requests from drivers</p>
            )}
          </motion.div>

          {/* Manager's Cash to Hand to Admin */}
          {(managerToAdmin.orders_to_handover?.length > 0 || managerToAdmin.orders_pending_approval?.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-wt-text-primary">Cash to Hand to Admin</h2>
                  <p className="text-wt-text-muted text-sm">Request admin to approve your handover</p>
                </div>
              </div>

              {/* Ready to hand over */}
              {managerToAdmin.orders_to_handover?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-wt-text-muted mb-3">Ready to Request ({formatCurrency(managerToAdmin.total_to_handover)})</h3>
                  <div className="space-y-2 mb-4">
                    {managerToAdmin.orders_to_handover.map(order => (
                      <div key={order.id} className="flex items-center gap-3 p-3 bg-wt-subtle rounded-lg">
                        <Checkbox
                          checked={selectedManagerOrders.includes(order.id)}
                          onCheckedChange={() => toggleOrderSelection(order.id, 'manager')}
                          className="border-blue-500 data-[state=checked]:bg-blue-500"
                        />
                        <div className="flex-1">
                          <p className="text-wt-text-primary text-sm">{order.order_number}</p>
                          <p className="text-xs text-wt-text-muted">{order.driver_name}</p>
                        </div>
                        <p className="text-blue-400 font-medium">{formatCurrency(order.cash_amount)}</p>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleRequestAdminHandover}
                    disabled={selectedManagerOrders.length === 0}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="request-admin-handover-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Request Admin Approval ({selectedManagerOrders.length})
                  </Button>
                </div>
              )}

              {/* Pending admin approval */}
              {managerToAdmin.orders_pending_approval?.length > 0 && (
                <div className="border-t border-wt-border pt-4">
                  <h3 className="text-sm font-medium text-yellow-400 mb-3">
                    Pending Admin Approval ({formatCurrency(managerToAdmin.total_pending_approval)})
                  </h3>
                  <div className="space-y-2">
                    {managerToAdmin.orders_pending_approval.map(order => (
                      <div key={order.id} className="flex items-center gap-3 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                        <Clock className="h-4 w-4 text-yellow-500" />
                        <span className="text-wt-text-primary text-sm flex-1">{order.order_number}</span>
                        <span className="text-yellow-400 font-medium">{formatCurrency(order.cash_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </>
      )}

      {/* ============ ADMIN VIEW ============ */}
      {(canCollectFromManager || isAdmin) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <DollarSign className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Manager Handover Requests</h2>
              <p className="text-wt-text-muted text-sm">Collect & settle cash held by managers — one flow, expenses netted</p>
            </div>
            {adminPending.total_pending > 0 && (
              <Badge className="ml-auto bg-purple-500/20 text-purple-400">
                {formatCurrency(adminPending.total_pending)}
              </Badge>
            )}
          </div>

          {adminPending.orders?.length > 0 ? (
            <div className="space-y-4">
              {Object.values(adminPending.orders.filter(o => o && o.id && o.order_number).reduce((g, o) => {
                const k = o.manager_id || 'unknown';
                (g[k] = g[k] || { manager_id: o.manager_id, manager_name: o.manager_name || 'Unknown manager', orders: [] }).orders.push(o);
                return g;
              }, {})).map((grp) => (
                <div key={grp.manager_id || 'unknown'} className="rounded-lg border border-wt-border/50 p-4" data-testid={`handover-group-${grp.manager_id || 'unknown'}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <p className="text-wt-text-primary font-medium">{grp.manager_name}</p>
                      <p className="text-xs text-wt-text-muted">
                        {grp.orders.length} order{grp.orders.length === 1 ? '' : 's'} · {formatCurrency(grp.orders.reduce((s, o) => s + (o.cash_amount || 0), 0))} cash held
                      </p>
                    </div>
                    {grp.manager_id ? (
                      <Button
                        onClick={() => adminCashRef.current?.openSettle(grp.manager_id)}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid={`settle-collect-${grp.manager_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Settle & Collect
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleApproveManagerHandover(grp.orders.map(o => o.id))}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="approve-unknown-manager-btn"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {grp.orders.map(order => (
                      <div key={order.id} className="flex items-center justify-between text-sm bg-wt-subtle rounded px-3 py-2" data-testid={`handover-order-${order.id}`}>
                        <span className="text-wt-text-primary">
                          {order.order_number}
                          <span className="text-wt-text-muted"> · {order.customer_name || 'Unknown Customer'}</span>
                        </span>
                        <span className="text-purple-400 font-medium">{formatCurrency(order.cash_amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-wt-text-muted">
                Settle & Collect opens the settlement for that manager — order cash, approved receipts and expenses are netted in one step, and any difference is recorded as a discrepancy.
              </p>
            </div>
          ) : (
            <p className="text-wt-text-muted text-center py-4">No pending handover requests from managers</p>
          )}
        </motion.div>
      )}

      {/* ============ POS REGISTER SHIFTS ============ */}
      {isAdmin && <PosShiftsPanel formatCurrency={formatCurrency} />}

      {/* ============ COURIER COD REMITTANCE ============ */}
      {(isAdmin || hasPermission('cash.courier_cod')) && (
        <CourierCodPanel formatCurrency={formatCurrency} />
      )}

      {/* ============ PAYMENT VERIFICATIONS (manually marked paid orders) ============ */}
      {(isAdmin || hasPermission('cash.approve_payments')) && (
        <PaymentVerificationPanel formatCurrency={formatCurrency} />
      )}

      {/* ============ MAMOPAY PAYMENTS SECTION ============ */}
      {(isAdmin || canCollectFromManager || canCollectFromDriver) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <CreditCard className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Link Payments</h2>
              <p className="text-wt-text-muted text-sm">All MamoPay & Stripe payment-link receipts across the app</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <ShopifyPaidSyncLog formatCurrency={formatCurrency} />
              <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30" data-testid="link-payments-count">
                {mamoPayments.length} Payments
              </Badge>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-wt-subtle rounded-lg p-4">
              <p className="text-wt-text-muted text-sm">Total Collected</p>
              <p className="text-2xl font-bold text-blue-400">
                {formatCurrency(mamoPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0))}
              </p>
            </div>
            <div className="bg-wt-subtle rounded-lg p-4">
              <p className="text-wt-text-muted text-sm">Total Payments</p>
              <p className="text-2xl font-bold text-wt-text-primary">{mamoPayments.length}</p>
            </div>
            <div className="bg-wt-subtle rounded-lg p-4">
              <p className="text-wt-text-muted text-sm">Today</p>
              <p className="text-2xl font-bold text-green-400">
                {mamoPayments.filter(p => {
                  const paidAt = p.paid_at;
                  if (!paidAt) return false;
                  return new Date(paidAt).toDateString() === new Date().toDateString();
                }).length}
              </p>
            </div>
            <div className="bg-wt-subtle rounded-lg p-4">
              <p className="text-wt-text-muted text-sm">This Week</p>
              <p className="text-2xl font-bold text-wt-gold">
                {mamoPayments.filter(p => {
                  const paidAt = p.paid_at;
                  if (!paidAt) return false;
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return new Date(paidAt) >= weekAgo;
                }).length}
              </p>
            </div>
          </div>

          {mamoLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400"></div>
            </div>
          ) : mamoPayments.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="link-payments-list">
              {mamoPayments.map(p => {
                const gw = (p.gateway || 'mamopay').toLowerCase();
                const gwLabel = gw === 'stripe' ? 'Stripe' : 'MamoPay';
                const gwColor = gw === 'stripe'
                  ? 'bg-violet-500/20 text-violet-300 border-violet-500/40'
                  : 'bg-blue-500/20 text-blue-300 border-blue-500/40';
                const refLabel = p.order_number
                  ? `#${p.order_number}`
                  : p.reference_type === 'lead'
                    ? 'Lead payment'
                    : (p.title || 'Payment link');
                const refHref = p.reference_type === 'order' && p.reference_id
                  ? `/orders?id=${p.reference_id}`
                  : null;
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 p-4 bg-wt-subtle rounded-lg hover:bg-wt-subtle/80 transition-colors"
                    data-testid={`link-payment-${p.id}`}
                  >
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <CreditCard className="h-4 w-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {refHref ? (
                          <a
                            href={refHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-wt-gold font-medium hover:underline"
                          >
                            {refLabel}
                          </a>
                        ) : (
                          <p className="text-wt-text-primary font-medium">{refLabel}</p>
                        )}
                        <Badge className={`text-[10px] border ${gwColor}`}>{gwLabel}</Badge>
                        <Badge className="bg-green-500/20 text-green-400 text-xs">Paid</Badge>
                      </div>
                      {(p.customer_name || p.customer_phone) && (
                        <p className="text-sm text-wt-text-muted truncate">
                          {p.customer_name || ''}{p.customer_name && p.customer_phone ? ' · ' : ''}{p.customer_phone || ''}
                        </p>
                      )}
                      {(p.mamopay_link_id || p.stripe_session_id) && (
                        <p className="text-xs text-wt-text-muted mt-0.5 font-mono truncate">
                          {gw === 'stripe' ? p.stripe_session_id : p.mamopay_link_id}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-blue-400 font-semibold">{formatCurrency(p.amount || 0)}</p>
                      <p className="text-xs text-wt-text-muted">
                        {p.paid_at && new Date(p.paid_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-wt-text-muted">
                        {p.paid_at && new Date(p.paid_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-wt-text-muted text-center py-8">No link payments recorded yet</p>
          )}
        </motion.div>
      )}

      {/* ============ MANAGER CASH WALLET & LEDGER ============ */}
      {(canCollectFromDriver || isAdmin) && (
        <ManagerCashPanel formatCurrency={formatCurrency} />
      )}

      {/* ============ ADMIN: MANAGER LEDGERS, APPROVALS & SETTLEMENTS ============ */}
      {(isAdmin || canCollectFromManager) && (
        <AdminCashPanel ref={adminCashRef} formatCurrency={formatCurrency} onSettled={loadCashData} />
      )}

      {/* No permissions message */}
      {!canViewCash && !canCollectCash && !canCollectFromDriver && !canCollectFromManager && !isAdmin && (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
          <p className="text-wt-text-muted">You don't have permission to view cash management.</p>
        </div>
      )}
    </div>
  );
};

export default CashManagementPage;
